import {Scrollbar} from '@forecast-it/design-system';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {createRefetchContainer, graphql} from 'react-relay';
import {useHistory} from 'react-router-dom';
import {getEffectiveLockedDate} from '../../../components/timesheets/time-lock/DeclinedWeekLockUtil';
import {usingInternalTimeApproval} from '../../shared/util/FeatureUtil';
import {APPROVAL_STATUS, GLOBAL_FILTER_FIELD, GLOBAL_FILTER_OPERATOR, HIDDEN_FEATURES} from '../../../constants';
import {PERMISSION_TYPE} from '../../../Permissions';
import {withSocketHandling} from '../../../socket/withSocketHandling';
import * as tracking from '../../../tracking';
import {useTrackPage} from '../../../tracking/amplitude/hooks/useTrackPage';
import {parseTaskFilters} from '../../shared/components/filters/filter_logic';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../shared/components/headers/top-header-bar/TopHeaderBar';
import TimeRegistrationModalCreateQueryWrapper from '../../shared/components/modals/time-registration/time-registration-modal/query-wrappers/TimeRegistrationModalCreateQueryWrapper';
import useEventListener from '../../shared/hooks/useEventListener';
import {useForecastFetchQuery} from '../../shared/hooks/useForecastFetchQuery';
import {useWindowSize} from '../../shared/hooks/useWindowSize';
import CompanySetupUtil, {isFeatureHidden} from '../../shared/util/CompanySetupUtil';
import {isDateDisabled} from '../../shared/util/DateUtil';
import {hasPermission} from '../../shared/util/PermissionsUtil';
import {hasTopDownProgramBudgetFeature} from '../../shared/util/ProgramFinancialLogic';
import {getTaskUrl, pathIncludesTask} from '../../shared/util/UrlUtil';
import Util from '../../shared/util/util';
import {lastWorkingDayInWeek as computeLastWorkingDayInWeek} from '../../shared/util/TimesheetApprovalStatus';
import {TimesheetsBottomSection} from './bottom-section/TimesheetsBottomSection';
import {getInitialState} from './bottom-section/TimesheetsFilters';
import {TimeRegistrationTargetSuggestionsQuery} from './time-registration-target-suggestions/TimeRegistrationTargetSuggestionsFetch';
import {getSocketConfig} from './timesheet_socket_handling';
import {TimesheetHeader} from './TimesheetHeader';
import {
	HeaderPageContainer,
	MyTimesheetLeftColumnWrapper,
	MyTimesheetsColumns,
	MyTimesheetsSideModal,
	MyTimesheetWrapper,
	PageWrapper,
} from './timesheets.styled';
import {TIME_PERIOD} from './timesheets_change_date_view';
import {useCalendarEvents} from './top-section/calendar-events/useCalendarEvents';
import TimesheetsTopSection from './top-section/timesheets_top_section';
import {useConvertedEventTracking} from './top-section/calendar-events/useConvertedEventTracking';
import {idFromGlobalId} from '../../shared/util/GlobalIdUtil';
import {useStoredState} from '../../shared/hooks/useStoredState';
import {TimesheetTableContextProvider} from './bottom-section/TimesheetTableContext';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';

const SHOW_CALENDAR_STORAGE_KEY = 'timesheet-v2-show-calendar';
const TIME_PERIOD_STORAGE_KEY = 'timesheet-time-period';

//This is duplicated in TimeRowUtils.ts, however it uses .endOfWeek and .subtract/.reverse instead.
const buildWeekArr = currentDate => {
	const weekArray = [];
	const rec = (date, acc, limit) => {
		if (limit > 0) {
			acc.push(date);
			rec(date.clone().add(1, 'days'), acc, limit - 1);
		}
		return acc;
	};
	return rec(currentDate.clone().startOf('week'), weekArray, 7);
};

const TimesheetsPageComponent = ({
	viewer,
	relay,
	setSocketConfig,
	setSocketVariables,
	selectedPersonId,
	setSelectedPersonId,
	currentViewingDate,
	setCurrentViewingDate,
	currentViewingMonth,
	setCurrentViewingMonth,
}) => {
	const {history} = useHistory();
	const {formatMessage} = useIntl();

	if (!viewer?.company) return <div></div>;
	if (!Util.AuthorizeViewerAccess('timesheets')) {
		const path = history.location.pathname;
		if (pathIncludesTask(path)) {
			history.push(getTaskUrl(path));
		} else {
			// if user doesnt have access rights to view this page redirect to no access page
			history.push('/not-authorized');
		}
		return <div></div>;
	}
	if (isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS)) {
		history.push('/');
	}

	const hasTimeRegistrationCreateAllPermission = hasPermission(PERMISSION_TYPE.TIME_REGISTRATION_CREATE_ALL);

	const customFieldDefinitions = viewer.company.customFieldDefinitionsV2?.edges.map(edge => edge.node) ?? [];

	const {fetch: fetchTimeRegistrationTargetSuggestions, data: timeRegistrationTargetSuggestionsData} = useForecastFetchQuery(
		TimeRegistrationTargetSuggestionsQuery
	);

	const [showCalendar, setShowCalendar] = useStoredState(SHOW_CALENDAR_STORAGE_KEY, false);
	const [timePeriod, setTimePeriod] = useStoredState(TIME_PERIOD_STORAGE_KEY, TIME_PERIOD.WEEK_VIEW);

	const weekArray = useMemo(
		() => (timePeriod === TIME_PERIOD.WEEK_VIEW ? buildWeekArr(currentViewingDate) : []),
		[currentViewingDate, timePeriod]
	);

	const [showAllTimeTasks, setShowAllTimeTasks] = useState(false);
	const [hideModal, setHideModal] = useState(window.innerWidth < 1240);
	const [searchString, setSearchString] = useState('');
	const [periodTimeApprovalStatus, setPeriodTimeApprovalStatus] = useState(APPROVAL_STATUS.NOT_SUBMITTED);
	const [hasTimeRegsInWeek, setHasTimeRegsInWeek] = useState(false);
	const [hasApprovableTimeInWeek, setHasApprovableTimeInWeek] = useState(false);
	// Latest declined day in the viewed week, or null. Read only by the submit end date, so that a
	// submit of the week in view reaches a declined row dated after today instead of stopping at
	// today and leaving the week declined — see getSubmitEndDate.
	const [lastDeclinedDateInWeek, setLastDeclinedDateInWeek] = useState(null);
	const [hasSubmittableTimeRegsInPeriod, setHasSubmittableTimeRegsInPeriod] = useState(false);
	const [clickedCalendarEventId, setClickedCalendarEventId] = useState(undefined);
	const {markEventAsConverted, isEventConverted: isEventConvertedBySideModal} = useConvertedEventTracking();

	const selectedPerson = viewer.company.person;
	const [filters, setFilters] = useState(getInitialState(selectedPerson.id));

	const hideWeekend = CompanySetupUtil.weekendsAlwaysHidden();
	const windowSize = useWindowSize();

	const minWidthForSideModal = hideWeekend ? 1727 - 240 : 1727;
	const sideModalInView = windowSize.width > minWidthForSideModal;

	const paginationRef = useRef(null);

	// Week-dependent: a week the approver has declined drops the person's own submission lock,
	// so the owner can add and remove the entries the decline is asking them to correct. Every
	// downstream lock affordance — the calendar padlocks, the day-cell click, the Create Time
	// Entry panel, the row delete/edit controls — reads this one value.
	const lockedDate = useMemo(
		() =>
			getEffectiveLockedDate(
				viewer.company,
				viewer.company.person,
				currentViewingDate,
				viewer.timeRegistrations?.edges,
				// Internal time runs through timesheet approval where the company approves it, so a
				// week declined only on internal time has to relax the lock here too — the server's
				// create and delete gates both count those rows.
				usingInternalTimeApproval(viewer.company.useInternalTimeApproval)
			),
		[viewer.company, viewer.company.person, currentViewingDate, viewer.timeRegistrations]
	);
	const nonViewerPersonSelected = viewer.actualPersonId !== selectedPersonId;

	const isWeekView = timePeriod === TIME_PERIOD.WEEK_VIEW;

	const currentViewingDateMemo = useMemo(() => currentViewingDate.clone(), [currentViewingDate]);
	const lastWorkingDayInWeek = useMemo(() => {
		// `weekArray.length === 0` here acts as a proxy for the not-yet-in-week-view state;
		// the shared helper recomputes the week from `currentViewingDate` internally, so we
		// don't pass `weekArray` itself. Keep the guard so first-render fallback stays
		// `currentViewingDate` (matching the prior inline behavior).
		if (timePeriod !== TIME_PERIOD.WEEK_VIEW || weekArray.length === 0) return currentViewingDate;
		return computeLastWorkingDayInWeek(selectedPerson, currentViewingDate);
	}, [timePeriod, weekArray.length, selectedPerson, currentViewingDate]);

	const isFullyLocked = useMemo(() => {
		if (lockedDate) {
			if (isWeekView) {
				const lastValidDateInWeek = CompanySetupUtil.weekendsAlwaysHidden()
					? lastWorkingDayInWeek
					: currentViewingDate.clone().endOf('week');
				return lockedDate.isSameOrAfter(lastValidDateInWeek, 'd');
			}

			return lockedDate.isSameOrAfter(currentViewingDate, 'd');
		}
		return false;
	}, [isWeekView, lockedDate, currentViewingDate, lastWorkingDayInWeek]);

	const googleSettings = viewer.calendarIntegrations.googleCalendar;
	const outlookSettings = viewer.calendarIntegrations.outlookCalendar;

	const showCalendarEvents =
		(!!googleSettings.enabledCalendars?.length || !!outlookSettings.enabledCalendars?.length) &&
		(!googleSettings.calendarTokenError || !outlookSettings.calendarTokenError) &&
		!nonViewerPersonSelected &&
		!!currentViewingDate;

	const {calendarEvents, isLoadingCalendarEvents} = useCalendarEvents(
		currentViewingDate,
		currentViewingMonth,
		showCalendarEvents
	);

	const refetch = (variables, renderVariables, callback, options) => {
		relay.refetch(variables, renderVariables, callback, options);
		setSocketVariables(variables);
	};

	const handleResize = useCallback(() => {
		setHideModal(window.innerWidth < 1240);
	}, [window.innerWidth]);

	useEventListener('resize', handleResize);

	useTrackPage('My Timesheet');

	useEffect(() => {
		document.title = 'Timesheets - Forecast';
		const taskIdFromUrl = Util.getUrlQueryParameter('showTaskId');
		if (taskIdFromUrl && hideModal) {
			showModal({
				type: MODAL_TYPE.CREATE_TIME_REGISTRATION,
				personId: viewer.actualPersonId,
				taskId: taskIdFromUrl,
			});
		}
		tracking.trackPage('Timesheet-page', null, null, taskIdFromUrl ? {calendarIntegration: true} : null);

		const selectedPersonIdInt = parseInt(atob(selectedPersonId).replace('Person:', ''));
		const actualPersonIdInt = parseInt(atob(viewer.actualPersonId).replace('Person:', ''));
		setSocketConfig(getSocketConfig([actualPersonIdInt, selectedPersonIdInt]), relay.refetch);
	}, []);

	useEffect(() => {
		fetchTimeRegistrationTargetSuggestions({
			timePeriod,
			currentViewingDate: currentViewingDate.format('YYYY-MM-DD'),
		});
	}, [currentViewingDate.format('YYYYMMDD'), timePeriod]);

	useEffect(() => {
		const selectedPersonIdInt = parseInt(atob(selectedPersonId).replace('Person:', ''));
		setSocketConfig(getSocketConfig(selectedPersonIdInt));
	}, [selectedPersonId]);

	const HeaderTitle = () => {
		const showTimeOffHelp = !viewer.company.bambooHREnabled;
		const showingViewer = selectedPersonId === viewer.actualPersonId;
		const content = [];
		const onboardingFlows = [
			{
				id: 'timesheets-new-improvements',
				title: 'New improvements to this page',
				description: null,
				contentId: 'agBtkGnlww',
			},

			{
				id: 'timesheets-introduction',
				title: 'Introduction to this page',
				description: null,
				contentId: '-y0nXpeCts',
			},
		];

		if (showTimeOffHelp) {
			onboardingFlows.push({
				id: 'timesheets-register-time-off',
				title: 'How to register time off',
				description: null,
				contentId: 'ju94wUwr7Y',
			});
		}

		if (hasTimeRegistrationCreateAllPermission) {
			onboardingFlows.push({
				id: 'timesheets-team-time',
				title: "Seeing your team's time registrations",
				contentId: '1681984211mAcq3994',
			});
		}

		const onboardingComponent = {
			id: 'onboarding-component',
			type: TopHeaderBar.TYPE.ONBOARDING,
			title: formatMessage({id: 'my_timesheets.onboarding_title'}),
			options: onboardingFlows,
			helpCenterLink: 'https://support.forecast.app/hc/en-us/sections/4461863590289-Managing-Time',
			subLink:
				'https://support.forecast.app/hc/en-us/articles/4611517711505-Viewing-and-managing-your-personal-timesheet',
		};

		content.push(onboardingComponent);

		return (
			<TopHeaderBarWrapper className="top-header-bar">
				<TopHeaderBar
					title={showingViewer ? formatMessage({id: 'timesheet.my_timesheet'}) : 'Team Member Timesheet'}
					content={content}
					withSkylineStyle
				/>
			</TopHeaderBarWrapper>
		);
	};

	const handleScroll = e => {
		if (paginationRef.current) {
			if (e.target.scrollHeight - e.target.scrollTop < e.target.clientHeight + 300) {
				paginationRef.current.loadMore();
			}
		}
	};

	const isCopyDisabled = isFullyLocked || isDateDisabled(currentViewingDate);

	const hasTopDownProgramBudgetFeatureFlag = hasTopDownProgramBudgetFeature();
	const processedCompanyProjectIds = [];
	const overBudgetProgramsByCompanyProjectId = viewer.timeRegistrations.edges
		.filter(timeRegistration => {
			const project = timeRegistration.node.task?.project
				? timeRegistration.node.task?.project
				: timeRegistration.node.project;
			const alreadyProcessed = processedCompanyProjectIds.includes(project?.companyProjectId);
			if (!alreadyProcessed && project?.companyProjectId) processedCompanyProjectIds.push(project?.companyProjectId);
			return (
				hasTopDownProgramBudgetFeatureFlag &&
				!alreadyProcessed &&
				project &&
				project.isOverBudgetProgramWarningApplicable
			);
		})
		.map(timeRegistration => {
			const project = timeRegistration.node.task?.project
				? timeRegistration.node.task?.project
				: timeRegistration.node.project;
			return {
				companyProjectId: project.companyProjectId,
				programInfo: project.program,
			};
		});

	const taskTypeSearchQuery = useMemo(() => {
		const weekStart = currentViewingDate.clone().startOf('week');
		const weekEnd = currentViewingDate.clone().endOf('week');
		const searchQuery = {filters: parseTaskFilters(filters, false, false)};
		if (searchString && searchString !== '') {
			const queryNameSearch = {
				operator: GLOBAL_FILTER_OPERATOR.CONTAINS,
				field: GLOBAL_FILTER_FIELD.NAME,
				value: [searchString],
			};
			searchQuery.filters.push(queryNameSearch);
		}
		searchQuery.filters.push(
			{
				field: GLOBAL_FILTER_FIELD.FILTER_BY_TIME_REG_SETTINGS,
				value: [
					isWeekView ? weekStart.format('YYYY-MM-DD') : currentViewingDate.format('YYYY-MM-DD'),
					isWeekView ? weekEnd.format('YYYY-MM-DD') : currentViewingDate.format('YYYY-MM-DD'),
					`${idFromGlobalId(selectedPerson.id)}`,
				],
			},
			{
				field: GLOBAL_FILTER_FIELD.PROJECT_MANUAL_PROGRESS,
				operator: GLOBAL_FILTER_OPERATOR.IN,
				value: ['false'],
			}
		);

		if (!showAllTimeTasks) {
			searchQuery.filters.push(
				{
					field: GLOBAL_FILTER_FIELD.START_DATE,
					operator: GLOBAL_FILTER_OPERATOR.LESS_OR_EQUAL,
					value: [isWeekView ? weekEnd.format('YYYY-MM-DD') : currentViewingDate.format('YYYY-MM-DD'), 'NULL'],
				},
				{
					field: GLOBAL_FILTER_FIELD.END_DATE,
					operator: GLOBAL_FILTER_OPERATOR.GREATER_OR_EQUAL,
					value: [isWeekView ? weekStart.format('YYYY-MM-DD') : currentViewingDate.format('YYYY-MM-DD'), 'NULL'],
				}
			);
		}

		return searchQuery;
	}, [currentViewingDate.format('YYYYMMDD'), isWeekView, filters, searchString, selectedPerson, showAllTimeTasks]);

	return (
		<PageWrapper data-cy={'timesheets-page'} overflowHidden>
			<HeaderPageContainer timeRegSideModalOpen={sideModalInView}>
				<HeaderTitle />
			</HeaderPageContainer>
			<HeaderPageContainer timeRegSideModalOpen={sideModalInView}>
				<TimesheetHeader
					selectedPersonId={selectedPersonId}
					setSelectedPersonId={setSelectedPersonId}
					currentViewingMonth={currentViewingMonth}
					showCalendar={showCalendar}
					currentViewingDate={currentViewingDate}
					timePeriod={timePeriod}
					setCurrentViewingDate={setCurrentViewingDate}
					setCurrentViewingMonth={setCurrentViewingMonth}
					setShowCalendar={setShowCalendar}
					setTimePeriod={setTimePeriod}
					searchString={searchString}
					setSearchString={setSearchString}
					showAddTimeRegButton={!sideModalInView}
					lastWorkingDayInWeek={lastWorkingDayInWeek}
					viewer={viewer}
					periodTimeApprovalStatus={periodTimeApprovalStatus}
					hasTimeRegsInWeek={hasTimeRegsInWeek}
					hasApprovableTimeInWeek={hasApprovableTimeInWeek}
					lastDeclinedDateInWeek={lastDeclinedDateInWeek}
					hasSubmittableTimeRegsInPeriod={hasSubmittableTimeRegsInPeriod}
				/>
			</HeaderPageContainer>
			<Scrollbar onScroll={handleScroll} isPageScroller>
				<MyTimesheetWrapper>
					<MyTimesheetsColumns>
						<MyTimesheetLeftColumnWrapper>
							<TimesheetsTopSection
								viewer={viewer}
								showCalendar={showCalendar}
								currentViewingDate={currentViewingDate}
								setCurrentViewingDate={setCurrentViewingDate}
								currentViewingMonth={currentViewingMonth}
								timePeriod={timePeriod}
								setTimePeriod={setTimePeriod}
								selectedPerson={selectedPerson}
								lockedDate={lockedDate}
								calendarEvents={calendarEvents}
								isLoadingCalendarEvents={isLoadingCalendarEvents}
								showNewSideModal={sideModalInView}
								setClickedCalendarEventId={setClickedCalendarEventId}
								isEventConvertedBySideModal={isEventConvertedBySideModal}
							/>
							<TimesheetTableContextProvider>
								<TimesheetsBottomSection
									paginationRef={paginationRef}
									currentViewingDate={currentViewingDateMemo}
									setCurrentViewingDate={setCurrentViewingDate}
									lastWorkingDayInWeek={lastWorkingDayInWeek}
									timeRegistrations={viewer.timeRegistrations.edges}
									timePeriod={timePeriod}
									setTimePeriod={setTimePeriod}
									nonViewerPersonSelected={nonViewerPersonSelected}
									selectedPerson={selectedPerson}
									lockedDate={lockedDate}
									customFieldDefinitions={customFieldDefinitions}
									overBudgetProgramsByCompanyProjectId={overBudgetProgramsByCompanyProjectId}
									timeRegistrationTargetSuggestionsData={timeRegistrationTargetSuggestionsData?.viewer}
									viewer={viewer}
									timeRegSideModalOpen={sideModalInView}
									isCopyDisabled={isCopyDisabled}
									selectedPersonId={selectedPersonId}
									filters={filters}
									setFilters={setFilters}
									showAllTimeTasks={showAllTimeTasks}
									setShowAllTimeTasks={setShowAllTimeTasks}
									searchActive={!!searchString}
									taskTypeSearchQuery={taskTypeSearchQuery}
									refetch={refetch}
									setPeriodTimeApprovalStatus={setPeriodTimeApprovalStatus}
									setHasTimeRegsInWeek={setHasTimeRegsInWeek}
									setHasApprovableTimeInWeek={setHasApprovableTimeInWeek}
									setLastDeclinedDateInWeek={setLastDeclinedDateInWeek}
									setHasSubmittableTimeRegsInPeriod={setHasSubmittableTimeRegsInPeriod}
								/>
							</TimesheetTableContextProvider>
						</MyTimesheetLeftColumnWrapper>
						{sideModalInView && (
							<MyTimesheetsSideModal data-cy="timesheet-page-side-modal">
								<TimeRegistrationModalCreateQueryWrapper
									showAsModal={false}
									personId={selectedPerson.id}
									showCloseButton={false}
									onCreateSuccessCallbacks={[() => markEventAsConverted(clickedCalendarEventId)]}
								/>
							</MyTimesheetsSideModal>
						)}
					</MyTimesheetsColumns>
				</MyTimesheetWrapper>
			</Scrollbar>
		</PageWrapper>
	);
};

export const TimesheetsPage = withSocketHandling(
	createRefetchContainer(
		TimesheetsPageComponent,
		{
			viewer: graphql`
				fragment TimesheetsPage_viewer on Viewer
				@argumentDefinitions(
					personId: {type: "ID"}
					startDateString: {type: "String"}
					endDateString: {type: "String"}
					fetchCustomFields: {type: "Boolean!"}
				) {
					id
					harvestUser
					unit4User
					actualPersonId
					email
					backendId
					language
					availableFeatureFlags {
						key
					}
					filters(first: 1000000, filterSection: MY_TIMESHEETS) @connection(key: "Viewer_filters", filters: []) {
						edges {
							node {
								id
								name
								section
								value
								updatedAt
							}
						}
					}
					calendarIntegrations {
						googleCalendar {
							calendarTokenError
							calendarFetchEnabled
							enabledCalendars
						}
						outlookCalendar {
							calendarTokenError
							calendarFetchEnabled
							enabledCalendars
						}
					}
					company {
						id
						useTimeApproval
						useInternalTimeApproval
						useTimeOffApproval
						harvestEnabled
						bambooHREnabled
						hiBobEnabled
						sageIntacctEnabled
						unit4Enabled
						characterLimit
						lockedPeriodYear
						lockedPeriodMonth
						lockedPeriodDay
						roles(includeDisabled: false) {
							edges {
								node {
									id
									name
								}
							}
						}
						modules {
							moduleType
						}
						internalTimes: idleTimes(first: 100000, internalTime: true, excludeDisabled: true) {
							totalCount
						}

						# TODO: Figure out if this is used.
						idleTimes(first: 100000) {
							edges {
								node {
									id
									name
									isInternalTime
									favoured
									disabled
								}
							}
						}
						person(id: $personId) {
							holidayCalendar {
								id
								holidayCalendarEntries(first: 10000) {
									edges {
										node {
											id
											day
											month
											year
											name
										}
									}
								}
								name
							}
							id
							role {
								id
								name
							}
							firstName
							lastName
							fullName
							createdAt
							startDate
							endDate
							systemUser
							initials
							active
							monday
							tuesday
							wednesday
							thursday
							friday
							saturday
							sunday
							excludeFromCompanyLockedPeriod
							submitLockedDateYear
							submitLockedDateMonth
							submitLockedDateDay
							harvestUser
							unit4User
							economicUser
							profilePictureId
							profilePictureDefaultId
							email
							weekApprovals(startDate: $startDateString, endDate: $endDateString) {
								id
								weekStartYear
								weekStartMonth
								weekStartDay
								approvalStatus
								declineReason
							}
						}
						customFieldDefinitionsV2(first: 1000)
							@connection(key: "Company_customFieldDefinitionsV2")
							@include(if: $fetchCustomFields) {
							edges {
								node {
									id
									key
									type
									displayName
									entityType
									... on TextCustomField {
										readOnly
									}
									... on IntegerCustomField {
										readOnly
									}
									... on DecimalCustomField {
										readOnly
									}
									... on DateCustomField {
										readOnly
									}
									... on DropdownCustomField {
										options {
											id
											displayName
										}
									}
								}
							}
						}
					}
					projects(first: 100000, excludeReadOnly: true, excludeRestricted: true) {
						edges {
							...ProjectDropdown_projects
							node {
								id
								name
								fullAccessToProject
								companyProjectId
								stage {
									id
									name
									profile
								}
								billable
								projectColor
								projectStartYear
								projectStartMonth
								projectStartDay
								projectEndYear
								projectEndMonth
								projectEndDay
								manualProgressOnProjectEnabled
								manualProgressOnPhasesEnabled
								manualProgressOnTasksEnabled
								projectPerson(personId: $personId) {
									role {
										id
										name
									}
								}
								harvestProject {
									id
								}
								unit4Project {
									id
									activitiesEnabled
								}
								isJiraProject
							}
						}
					}
					timeRegistrations(
						first: 100000
						personId: $personId
						startDate: $startDateString
						endDate: $endDateString
						verifyPermissionRights: false
					) @connection(key: "Viewer_timeRegistrations", filters: []) {
						edges {
							node {
								id
								day
								month
								year
								minutesRegistered
								billableMinutesRegistered
								updatedAt
								role {
									id
									name
								}
								notes
								invoiced
								xeroInvoiceId
								lockedInPeriod
								harvestTimeId
								allocationId
								harvestTaskIdInt
								harvestError
								unit4ActivityId
								approvalStatus
								declineReason
								hiBobAllocationId
								project {
									id
									name
									stage {
										id
										name
										profile
									}
									projectColor
									companyProjectId
									estimationUnit
									fullAccessToProject
									isOverBudgetProgramWarningApplicable
									program {
										name
										prefix
										budgetType
										members {
											edges {
												node {
													role
													person {
														id
													}
												}
											}
										}
									}
									billable
									projectStartYear
									projectStartMonth
									projectStartDay
									projectEndYear
									projectEndMonth
									projectEndDay
									manualProgressOnProjectEnabled
									manualProgressOnPhasesEnabled
									manualProgressOnTasksEnabled
									projectPerson(personId: $personId) {
										role {
											id
											name
										}
									}
									harvestProject {
										id
										name
									}
									unit4Project {
										id
										name
										activitiesEnabled
									}
									client {
										id
										name
									}
									# Project Indicator - not fragment because of JS/TS incompatibilty
									name
									companyProjectId
									customProjectId
									projectColor
								}
								task {
									id
									name
									progress
									companyTaskId
									jiraId
									highPriority
									approved
									timeLeft
									favoured
									fullAccessToProject
									latestUiUpdateAt
									timeLeft
									estimateForecastMinutes
									totalMinutesRegistered
									billable
									parentTaskId
									parentTask {
										name
										companyTaskId
									}
									startYear
									startMonth
									startDay
									deadlineYear
									deadlineMonth
									deadlineDay
									statusColumnV2 {
										category
									}
									assignedPersons {
										id
									}
									project {
										id
										name
										stage {
											id
											name
											profile
										}
										companyProjectId
										isOverBudgetProgramWarningApplicable
										program {
											name
											prefix
											budgetType
											members {
												edges {
													node {
														role
														person {
															id
														}
													}
												}
											}
										}
										billable
										projectColor
										estimationUnit
										fullAccessToProject
										projectStartYear
										projectStartMonth
										projectStartDay
										projectEndYear
										projectEndMonth
										projectEndDay
										manualProgressOnProjectEnabled
										manualProgressOnPhasesEnabled
										manualProgressOnTasksEnabled
										projectPerson(personId: $personId) {
											role {
												id
												name
											}
										}
										client {
											id
											name
										}
										harvestProject {
											id
											name
										}
										unit4Project {
											id
											name
											activitiesEnabled
										}
										# Project Indicator - not fragment because of JS/TS incompatibilty
										name
										companyProjectId
										customProjectId
										projectColor
									}
									phase {
										id
										name
									}
								}
								idleTime {
									id
									name
									isInternalTime
									favoured
									disabled
								}
								person {
									id
									firstName
									lastName
									isViewer
									role {
										id
										name
									}
								}
								customFields(first: 1000, fetchCustomFields: $fetchCustomFields)
									@connection(key: "TimeRegistrations_customFields") {
									edges {
										node {
											__typename
											key
											displayName
											value
											displayValue
											... on TextCustomFieldValue {
												readOnly
											}
											... on IntegerCustomFieldValue {
												readOnly
											}
											... on DecimalCustomFieldValue {
												readOnly
											}
											... on DateCustomFieldValue {
												readOnly
											}
											... on DropdownCustomFieldValue {
												options {
													id
													displayName
												}
											}
										}
									}
								}
							}
						}
					}
				}
			`,
		},
		graphql`
			query TimesheetsPageRefetchQuery(
				$personId: ID
				$startDateString: String
				$endDateString: String
				$fetchCustomFields: Boolean!
			) {
				viewer {
					component(name: "timesheet")
					...TimesheetsPage_viewer
						@arguments(
							personId: $personId
							startDateString: $startDateString
							endDateString: $endDateString
							fetchCustomFields: $fetchCustomFields
						)
				}
			}
		`
	)
);
